import { Utils } from "@/common/utils";

/** UI status type used on Effectiveness Plans page (status circles). */
export type DrillDownStatusType = "open" | "achieved" | "unmet" | "no_date_assigned" | "overdue";

/** API composite status values accepted by Action Plans list endpoint. */
const STATUS_TYPE_TO_API: Record<DrillDownStatusType, string> = {
    open: "open_time",
    achieved: "closed_goal_met",
    unmet: "closed_goal_not_met",
    no_date_assigned: "no_date_assigned",
    overdue: "open_late",
};

/** Display labels for the Action Plans page filter indicator. */
const API_STATUS_TO_LABEL: Record<string, string> = {
    open_time: "Open (on time)",
    open_late: "Overdue",
    closed_goal_met: "Achieved",
    closed_goal_not_met: "Unmet",
    no_date_assigned: "No Date Assigned",
};

/**
 * Maps UI status type to API status value for Action Plans filtering.
 */
export function getApiStatusFromType(statusType: DrillDownStatusType): string {
    return STATUS_TYPE_TO_API[statusType];
}

/**
 * Maps API status value to display label for filter indicator.
 */
export function getStatusLabelFromApi(apiStatus: string): string {
    return API_STATUS_TO_LABEL[apiStatus] ?? apiStatus;
}

/**
 * Maps UI status type to display label (for tooltips, aria-labels).
 */
export function getStatusLabelFromType(statusType: DrillDownStatusType): string {
    return API_STATUS_TO_LABEL[STATUS_TYPE_TO_API[statusType]] ?? statusType;
}

/**
 * Builds the Action Plans page URL with drill-down query params.
 * Uses manual query building so it works in test env where URLSearchParams may be mocked without .set/.toString.
 */
export function getActionPlanDrillDownUrl(
    planRuuid: string,
    statusType: DrillDownStatusType,
    planName?: string
): string {
    const base = Utils.getActionplanUrl();
    const parts = [
        `strategic_plan_ruuid=${encodeURIComponent(planRuuid)}`,
        `status=${encodeURIComponent(getApiStatusFromType(statusType))}`,
    ];
    if (planName?.trim()) {
        parts.push(`strategic_plan_name=${encodeURIComponent(planName.trim())}`);
    }
    return `${base}?${parts.join("&")}`;
}
